import { useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { BarChartCard } from "@/components/charts/BarChartCard";
import { PieChartCard } from "@/components/charts/PieChartCard";
import { PostsTable } from "@/components/tables/PostsTable";
import { TopIGPosts } from "@/components/cards/TopPostCard";
import { EngagementDistribution, WorstPosts, RestaurantLeaderboard, ViralPosts, FollowerTrend } from "@/components/charts/PerformanceAnalytics";
import { FilterBar } from "@/components/layout/FilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFilteredIG } from "@/hooks/useFilteredData";
import { fillDateRange } from "@/lib/chart-utils";
import { getPostTypeLabel } from "@/lib/post-type-labels";
import { getPreviousPeriod } from "@/lib/period-compare";
import { Eye, Heart, MessageCircle, Bookmark, Play, Hash, TrendingUp, BarChart3 } from "lucide-react";
import { LoadingState } from '@/components/ui/spinner';

export default function InstagramPage() {
  const { data, allData, accounts, postTypes, filters, setFilters, loading } = useFilteredIG();
  const posts = data.filter((p) => p.media_id);

  const totalReach = posts.reduce((s, p) => s + p.media_reach, 0);
  const totalEng = posts.reduce((s, p) => s + p.media_engagement, 0);
  const totalLikes = posts.reduce((s, p) => s + p.media_like_count, 0);
  const totalComments = posts.reduce((s, p) => s + p.media_comments_count, 0);
  const totalSaves = posts.reduce((s, p) => s + p.media_saved, 0);
  const totalReelViews = posts.reduce((s, p) => s + p.media_reel_video_views, 0);
  const engRate = totalReach > 0 ? totalEng / totalReach : 0;
  const avgReach = posts.length > 0 ? Math.round(totalReach / posts.length) : 0;
  const avgEng = posts.length > 0 ? Math.round(totalEng / posts.length) : 0;

  const prev = useMemo(() => {
    const { dateFrom, dateTo } = getPreviousPeriod(filters.dateFrom, filters.dateTo);
    const pp = allData.filter((p) => p.media_id && p.date >= dateFrom && p.date <= dateTo);
    const reach = pp.reduce((s, p) => s + p.media_reach, 0);
    const eng = pp.reduce((s, p) => s + p.media_engagement, 0);
    return {
      posts: pp.length, reach, eng,
      engRate: reach > 0 ? eng / reach : 0,
      avgReach: pp.length > 0 ? Math.round(reach / pp.length) : 0,
      avgEng: pp.length > 0 ? Math.round(eng / pp.length) : 0,
      likes: pp.reduce((s, p) => s + p.media_like_count, 0),
      comments: pp.reduce((s, p) => s + p.media_comments_count, 0),
      saves: pp.reduce((s, p) => s + p.media_saved, 0),
      reels: pp.reduce((s, p) => s + p.media_reel_video_views, 0),
    };
  }, [allData, filters.dateFrom, filters.dateTo]);

  const reachTrend = useMemo(() => {
    const map = new Map<string, number>();
    posts.forEach((p) => map.set(p.date, (map.get(p.date) || 0) + p.media_reach));
    const raw = Array.from(map.entries()).map(([date, value]) => ({ date, value }));
    return fillDateRange(raw, filters.dateFrom, filters.dateTo);
  }, [posts, filters.dateFrom, filters.dateTo]);

  const typePie = useMemo(() => {
    const map = new Map<string, number>();
    posts.forEach((p) => { const t = p.media_product_type || p.media_type || "unknown"; map.set(t, (map.get(t) || 0) + 1); });
    return Array.from(map.entries()).map(([name, value]) => ({ name: getPostTypeLabel(name), value }));
  }, [posts]);

  const accountData = useMemo(() => {
    const map = new Map<string, { reach: number; engagement: number }>();
    posts.forEach((p) => {
      const curr = map.get(p.account_name) || { reach: 0, engagement: 0 };
      curr.reach += p.media_reach; curr.engagement += p.media_engagement;
      map.set(p.account_name, curr);
    });
    return Array.from(map.entries()).map(([name, { reach, engagement }]) => ({ name, value: reach, value2: engagement }));
  }, [posts]);

  if (loading) return <LoadingState label="Načítám data…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Instagram" description="Detailní analýza příspěvků" />

      <FilterBar filters={filters} onChange={setFilters} showPostType accounts={accounts} postTypes={postTypes} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Příspěvků" value={posts.length} previousValue={prev.posts} icon={<Hash className="h-4 w-4 text-primary" />} />
        <MetricCard label="Eng. rate" value={engRate} previousValue={prev.engRate} format="percent" icon={<TrendingUp className="h-4 w-4 text-primary" />} />
        <MetricCard label="Ø dosah" value={avgReach} previousValue={prev.avgReach} icon={<Eye className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Ø eng." value={avgEng} previousValue={prev.avgEng} icon={<BarChart3 className="h-4 w-4 text-primary" />} subtitle="na post" />
        <MetricCard label="Lajky" value={totalLikes} previousValue={prev.likes} icon={<Heart className="h-4 w-4 text-primary" />} />
        <MetricCard label="Komentáře" value={totalComments} previousValue={prev.comments} icon={<MessageCircle className="h-4 w-4 text-primary" />} />
        <MetricCard label="Uložení" value={totalSaves} previousValue={prev.saves} icon={<Bookmark className="h-4 w-4 text-primary" />} />
        <MetricCard label="Reels views" value={totalReelViews} previousValue={prev.reels} icon={<Play className="h-4 w-4 text-primary" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart title="Trend dosahu" data={reachTrend} color="#d4816b" label1="Dosah" igPosts={posts} />
        <BarChartCard title="Porovnání účtů" data={accountData} color="#d4816b" color2="#e8c36a" label1="Dosah" label2="Engagement" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PieChartCard title="Typy příspěvků" data={typePie} />
        <TopIGPosts posts={posts} limit={3} title="Top engagement" />
        <TopIGPosts posts={posts} limit={3} title="Top dosah" sortBy="reach" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ViralPosts igPosts={posts} />
        <WorstPosts igPosts={posts} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementDistribution fbPosts={[]} igPosts={posts} />
        <FollowerTrend igPosts={data} platform="IG" />
      </div>

      <RestaurantLeaderboard igPosts={posts} />

      <div>
        <h2 className="text-lg font-semibold mb-4">Všechny příspěvky ({posts.length})</h2>
        <PostsTable posts={posts} platform="instagram" />
      </div>
    </div>
  );
}
